import clsx from 'clsx';
import React from 'react';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { useInboxEntryPanel } from '@/src/components/project/Inbox2/store/inboxEntryPanel.store';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import { showToast } from '@/src/globalFunction';
import { useToggle } from '@/src/hooks/useToggle';

import { useValidateActionResponse } from '../../../../useValidateActionResponse';
import { usePermanentDeleteFile } from '../../../api/permanentDeleteFile';
import ConfirmArchiveDocumentModal from '../DeleteFile/ArchiveDocumentModal/ConfirmArchiveDocumentModal/ConfirmArchiveDocumentModal';

const subject = 'file';

type PermanentDeleteFileProps = {
  fileEntry: FileEntry;
  enabled: boolean;
};

const PermanentDeleteFile = ({ fileEntry, enabled = true }: PermanentDeleteFileProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const [showConfirmationModal, toggleShowConfirmationModal] = useToggle(false);
  const { setInboxEntry } = useInboxEntryPanel();
  const permanentdeleteFileMutation = usePermanentDeleteFile({ projectId });
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(permanentdeleteFileMutation.data)
      ? permanentdeleteFileMutation.data
      : null,
  });
  const handlePermanentDelete = async () => {
    const response = await permanentdeleteFileMutation.mutateAsync({
      fileId: fileEntry.id,
    });

    if (response) {
      toggleShowConfirmationModal(false);
      setInboxEntry(null);
      showToast('success', `${fileEntry.file.name} has been Deleted.'`);
    }
  };

  return (
    <React.Fragment>
      {permanentdeleteFileMutation.isLoading && <Spinner />}
      <ActionButton
        id={'delete_file'}
        className={clsx('tw-text-cfo-red-400', 'hover:tw-bg-red-100')}
        icon={ICONS.delete}
        iconClasses={clsx('tw-text-cfo-red-400', 'group-hover:tw-text-cfo-red-400')}
        onClick={toggleShowConfirmationModal}
        tooltipSettings={{
          content: 'Delete',
          place: 'left',
        }}
        disabled={!enabled}
      />

      {showConfirmationModal && (
        <ModalPortal>
          <ConfirmArchiveDocumentModal
            subject={subject}
            bodySubject={subject}
            onSubmit={handlePermanentDelete}
            onClose={toggleShowConfirmationModal}
            permanentDelete={true}
          />
        </ModalPortal>
      )}
      {confirmRefreshModal}
    </React.Fragment>
  );
};

export default PermanentDeleteFile;
